/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — persistent on-canvas legend overlay (Josh grant
   2026-08-19: "show the legend on screen when not in edit mode").
   Compact, unobtrusive corner card — same single-source legend.ts
   data + the same LegendContent row rendering the old popover used.
   Mounted by OrgChartV2 only when `!editable && legendVisible`; the
   toolbar's Legend toggle controls `legendVisible`, persisted like
   every other chart UI pref. Light+dark safe — tokens only.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Node as RFNode } from '@xyflow/react';
import { Info } from 'lucide-react';
import { Icon } from '../../../components/ui/Icon';
import { rem } from '../../../lib/rem';
import { computeChartLegend, isChartLegendEmpty } from './legend';
import { LegendContent } from './LegendContent';

export function LegendOverlay({ nodes }: { nodes: RFNode[] }) {
  const legend = computeChartLegend(nodes);
  if (isChartLegendEmpty(legend)) return null;

  return (
    <div
      style={{
        position: 'absolute',
        left: 'var(--space-3)',
        bottom: 'var(--space-3)',
        zIndex: 5,
        width: rem(210),
        maxHeight: rem(260),
        overflowY: 'auto',
        background: 'color-mix(in srgb, var(--sa) 92%, transparent)',
        border: '1px solid var(--bd)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: '0 8px 24px rgba(0,0,0,0.14)',
        padding: 'var(--space-2) ' + rem(10),
        pointerEvents: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: rem(6),
          fontSize: 'var(--font-size-sm)',
          fontWeight: 700,
          color: 'var(--tm)',
          textTransform: 'uppercase',
          letterSpacing: '.04em',
          marginBottom: 'var(--space-1)',
        }}
      >
        <Icon icon={Info} em={1.0909} aria-hidden="true" />
        Legend
      </div>
      <LegendContent nodes={nodes} />
    </div>
  );
}
